import React from "react";
import { X, Money, CreditCard } from "@phosphor-icons/react";

const HISTORY_PILL = (status) => (status === "completed" ? "pill-online" : "pill-error");

function PaymentBadge({ method }) {
  if (!method) return null;
  const isCard = method === "card";
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600" data-testid="history-payment-method">
      {isCard ? <CreditCard size={14} weight="duotone" /> : <Money size={14} weight="duotone" />}
      Cobrar en {isCard ? "tarjeta" : "efectivo"}
    </span>
  );
}

export function DriverProfileModal({ driverProfile, onSubmit, onClose, embedded }) {
  const fields = (
    <>
      <div className="space-y-3">
        <div><label className="label">Modelo</label><input name="vehicle_model" className="input-field" required defaultValue={driverProfile?.vehicle_model} data-testid="vehicle-model-input" /></div>
        <div><label className="label">Placa</label><input name="vehicle_plate" className="input-field" required defaultValue={driverProfile?.vehicle_plate} data-testid="vehicle-plate-input" /></div>
        <div><label className="label">Color</label><input name="vehicle_color" className="input-field" defaultValue={driverProfile?.vehicle_color} data-testid="vehicle-color-input" /></div>
        <div><label className="label">Licencia</label><input name="license_number" className="input-field" defaultValue={driverProfile?.license_number} data-testid="vehicle-license-input" /></div>
      </div>
      <h3 className="font-display text-lg font-bold mt-5 mb-1">Mi tarifa</h3>
      <p className="text-xs text-slate-500 mb-3">Nocturno: 21:00–06:00. Precio = puesta de bandera + km × precio/km.</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Bandera diurna (€)</label>
          <input name="base_fee_day" type="number" step="0.01" min="0" className="input-field" required defaultValue={driverProfile?.base_fee_day ?? 2.0} data-testid="base-fee-day-input" />
        </div>
        <div>
          <label className="label">Bandera nocturna (€)</label>
          <input name="base_fee_night" type="number" step="0.01" min="0" className="input-field" required defaultValue={driverProfile?.base_fee_night ?? 2.5} data-testid="base-fee-night-input" />
        </div>
        <div>
          <label className="label">€/km diurno</label>
          <input name="rate_per_km_day" type="number" step="0.01" min="0" className="input-field" required defaultValue={driverProfile?.rate_per_km_day ?? 1.5} data-testid="rate-km-day-input" />
        </div>
        <div>
          <label className="label">€/km nocturno</label>
          <input name="rate_per_km_night" type="number" step="0.01" min="0" className="input-field" required defaultValue={driverProfile?.rate_per_km_night ?? 1.5} data-testid="rate-km-night-input" />
        </div>
      </div>
    </>
  );

  if (embedded) {
    return (
      <form onSubmit={onSubmit} className="card" data-testid="vehicle-form">
        {fields}
        <button type="submit" className="btn-primary w-full mt-5" data-testid="save-profile-btn">Guardar cambios</button>
      </form>
    );
  }

  return (
    <div className="absolute inset-0 z-[1100] bg-slate-900/50 flex items-center justify-center p-4" data-testid="profile-modal">
      <form onSubmit={onSubmit} className="bg-white rounded-3xl p-8 w-full max-w-md fade-in max-h-[90vh] overflow-auto">
        <h2 className="font-display text-2xl font-bold mb-4">Datos del vehículo</h2>
        {fields}
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cerrar</button>
          <button type="submit" className="btn-primary flex-1" data-testid="save-profile-btn">Guardar</button>
        </div>
      </form>
    </div>
  );
}

export function DriverHistorySheet({ history, onClose, embedded }) {
  const list = (
    <div className="space-y-3 scroll-area">
      {history.length === 0 && <p className="text-slate-500 text-center py-8">Sin viajes aún</p>}
      {history.map((r) => (
        <div key={r.id} className="card">
          <div className="flex justify-between mb-2">
            <span className={`pill ${HISTORY_PILL(r.status)}`}>{r.status}</span>
            <span className="font-bold" data-testid={`driver-history-amount-${r.id}`}>€{(r.final_price || r.estimated_price || 0).toFixed(2)}</span>
          </div>
          <div className="text-sm text-slate-600">{r.passenger_name} · {r.pickup_address} → {r.dest_address}</div>
          {r.operated_by_name && (
            <div className="text-xs font-semibold mt-1" style={{ color: "var(--primary)" }} data-testid={`ride-operator-${r.id}`}>
              Conducido por {r.operated_by_name}
            </div>
          )}
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</span>
            <PaymentBadge method={r.payment_method} />
          </div>
        </div>
      ))}
    </div>
  );

  if (embedded) {
    return (
      <div className="app-section" data-testid="driver-history-section">
        <h2 className="section-title font-display">Mis viajes</h2>
        {list}
      </div>
    );
  }

  return (
    <div className="bottom-sheet slide-up" style={{ maxHeight: "70vh", overflow: "auto" }} data-testid="driver-history-sheet">
      <div className="handle" />
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-display font-bold">Mis viajes</h2>
        <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><X size={18} /></button>
      </div>
      {list}
    </div>
  );
}
